import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spin, Empty, Alert, Card, Button, Row, Col } from 'antd';
import { MergeCellsOutlined, ReloadOutlined } from '@ant-design/icons';
import { adminContactService } from '../../../services/api/adminContactService';
import type { DuplicateGroup } from '../../../types/contact';
import { DuplicateGroupCard } from './components/DuplicateGroupCard';
import { MergeContactsModal } from './components/MergeContactsModal';
import { PageHeader } from '../../../components/common/PageHeader';

export const AdminContactDuplicatesView: React.FC = () => {
  const { t } = useTranslation();
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<DuplicateGroup | null>(null);

  useEffect(() => {
    loadDuplicates();
  }, []);

  const loadDuplicates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminContactService.getDuplicates();
      setDuplicateGroups(response.results);
    } catch (err: any) {
      setError(err.message || t('admin.contacts.error.load_duplicates'));
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async (primaryId: string, duplicateIds: string[]) => {
    try {
      await adminContactService.mergeContacts({
        primary_contact_id: primaryId,
        duplicate_contact_ids: duplicateIds,
      });
      await loadDuplicates();
      setSelectedGroup(null);
    } catch (err: any) {
      throw err;
    }
  };

  return (
    <Card>
      <PageHeader
        icon={<MergeCellsOutlined />}
        accent="#722ED1"
        title={t('admin.contacts.duplicates.title', 'Fusionar contactos duplicados')}
        subtitle={t(
          'admin.contacts.duplicates.subtitle',
          'Grupos de UnifiedContact con email, teléfono o NIF coincidentes',
        )}
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void loadDuplicates()}
            loading={loading}
          >
            {t('common.reload', 'Recargar')}
          </Button>
        }
      />

      {error ? (
        <Alert
          message={t('common.error')}
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : duplicateGroups.length === 0 ? (
        <Empty
          description={t(
            'admin.contacts.no_duplicates',
            'No se han detectado contactos duplicados',
          )}
        />
      ) : (
        <Row gutter={[16, 16]}>
          {duplicateGroups.map((group) => (
            <Col
              key={`${group.match_field}-${group.match_value}`}
              xs={24}
              md={12}
              lg={8}
              xxl={6}
              style={{ display: 'flex' }}
            >
              <DuplicateGroupCard
                group={group}
                onMerge={() => setSelectedGroup(group)}
              />
            </Col>
          ))}
        </Row>
      )}

      {selectedGroup && (
        <MergeContactsModal
          group={selectedGroup}
          onClose={() => setSelectedGroup(null)}
          onMerge={handleMerge}
        />
      )}
    </Card>
  );
};
